import React, { useRef } from 'react';
import { useTheme } from '../context/ThemeContext';

export const MAX_IMAGE_FILES = 6;

export interface UploadedFile {
  name: string;
  content?: string; // Base64 content for images (for UI preview and LLM)
  type: string;
  size: number;
  file: File; // The actual File object for upload to Blob
  download_url?: string; // Full download URL (set after upload)
}

interface FileUploadProps {
  uploadedFiles: UploadedFile[];
  onFileUpload: (event: React.ChangeEvent<HTMLInputElement>) => void;
  onDeleteFile: (index: number) => void;
}

const FileUpload: React.FC<FileUploadProps> = ({
  uploadedFiles,
  onFileUpload,
  onDeleteFile
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const { colors } = useTheme();

  const handleFileButtonClick = () => {
    fileInputRef.current?.click();
  };

  const hasImages = uploadedFiles.length > 0 && uploadedFiles[0].type.startsWith('image/');
  const hasNonImage = uploadedFiles.length > 0 && !uploadedFiles[0].type.startsWith('image/');
  const canAddMore = uploadedFiles.length < MAX_IMAGE_FILES;

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept=".txt,.csv,.pdf,.png,.jpg,.jpeg,.xlsx,.docx,.pptx"
        multiple
        onChange={onFileUpload}
        style={{ display: 'none' }}
      />
      {uploadedFiles.length > 0 && (
        <div style={{
          backgroundColor: colors.surfaceElevated,
          borderRadius: '12px',
          padding: '10px 15px',
          marginBottom: '15px',
          border: `1px solid ${colors.border}`
        }}>
          {hasImages ? (
            // Image previews — horizontal row
            <div style={{ display: 'flex', gap: '10px', flexWrap: 'wrap' }}>
              {uploadedFiles.map((uploadedFile, index) => (
                <div key={index} style={{ position: 'relative' }}>
                  <img
                    src={uploadedFile.content ? `data:${uploadedFile.type};base64,${uploadedFile.content}` : ''}
                    alt={uploadedFile.name}
                    style={{
                      maxWidth: '150px',
                      maxHeight: '120px',
                      borderRadius: '8px',
                      objectFit: 'cover',
                      display: 'block'
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => onDeleteFile(index)}
                    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.7)'; }}
                    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'rgba(0,0,0,0.5)'; }}
                    style={{
                      position: 'absolute',
                      top: '4px',
                      right: '4px',
                      background: 'rgba(0,0,0,0.5)',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      width: '22px',
                      height: '22px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      color: 'white',
                      fontSize: '13px',
                      fontWeight: '600',
                      transition: 'background-color 0.2s ease',
                      lineHeight: 1
                    }}
                  >
                    ×
                  </button>
                  <div style={{ color: colors.textMuted, fontSize: '11px', marginTop: '2px', maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {uploadedFile.name}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            // Non-image file list
            <div style={{ display: 'flex', flexDirection: 'column', gap: '6px' }}>
              {uploadedFiles.map((uploadedFile, index) => (
                <div key={index} style={{
                  display: 'flex',
                  alignItems: 'center'
                }}>
                  <div style={{
                    backgroundColor: colors.surfaceElevated,
                    borderRadius: '8px',
                    padding: '6px 8px',
                    marginRight: '12px'
                  }}>
                    <span role="img" aria-label="Document">📄</span>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 500, fontSize: '14px', color: colors.textPrimary }}>{uploadedFile.name}</div>
                    <div style={{ color: colors.textMuted, fontSize: '12px' }}>
                      Document {uploadedFile.size < 1024 ? `${uploadedFile.size} B` : `${(uploadedFile.size / 1024).toFixed(1)} KB`}
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => onDeleteFile(index)}
                    onMouseEnter={(e) => {
                      e.currentTarget.style.color = colors.errorHover;
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.color = colors.textMuted;
                    }}
                    style={{
                      background: 'none',
                      border: 'none',
                      cursor: 'pointer',
                      borderRadius: '50%',
                      width: '35px',
                      height: '35px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: 0,
                      color: colors.textMuted,
                      fontSize: '18px',
                      fontWeight: '600',
                      transition: 'color 0.2s ease'
                    }}
                  >
                    X
                  </button>
                </div>
              ))}
            </div>
          )}
          {hasNonImage && uploadedFiles.some(f =>
            f.type === 'application/pdf' ||
            f.type === 'application/vnd.openxmlformats-officedocument.wordprocessingml.document' ||
            f.type === 'application/vnd.openxmlformats-officedocument.presentationml.presentation'
          ) && (
            <div style={{
              marginTop: '8px',
              padding: '6px 10px',
              backgroundColor: 'rgba(217, 164, 6, 0.1)',
              border: '1px solid rgba(217, 164, 6, 0.3)',
              borderRadius: '8px',
              fontSize: '12px',
              color: colors.textSecondary
            }}>
              ⚠ Images within this file will not be seen. Upload them separately if needed.
            </div>
          )}
        </div>
      )}
      <button
        type="button"
        onClick={handleFileButtonClick}
        disabled={!canAddMore}
        style={{
          position: 'absolute',
          left: '12px',
          bottom: '16px',
          backgroundColor: colors.textMuted,
          color: 'white',
          border: 'none',
          borderRadius: '20px',
          width: '40px',
          height: '40px',
          fontWeight: '600',
          fontSize: '16px',
          cursor: canAddMore ? 'pointer' : 'not-allowed',
          transition: 'all 0.2s ease',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          opacity: canAddMore ? 1 : 0.5,
          background: `linear-gradient(135deg, ${colors.textMuted} 0%, ${colors.textSecondary} 100%)`
        }}
        title={canAddMore ? `Upload file or paste image (Ctrl+V)${uploadedFiles.length > 0 ? ` — ${MAX_IMAGE_FILES - uploadedFiles.length} more allowed` : ''}` : `Maximum ${MAX_IMAGE_FILES} files`}
      >
        +
      </button>
    </>
  );
};

export default FileUpload;
